<script setup>
import { ref, computed, watch } from 'vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconEye from '@/components/icon/IconEye.vue'
import IconEyeCrossed from '@/components/icon/IconEyeCrossed.vue'
import IconCross from '@/components/icon/IconCross.vue'
import IconPlusTiny from '@/components/icon/IconPlusTiny.vue'
import LanguageSelect from './LanguageSelect.vue'
import {
	FALLBACK_ENGINE_INFO,
	BUFFER_HINT,
	PASSPHRASE_RULE,
	REGION_NAMES,
	POSITION_OPTIONS,
	autoRegionLabel,
	spokenOptions,
	withCurrent,
	ALIGNMENT_OPTIONS,
	TEXT_COLOR_SWATCHES,
	withTeletextReasons,
	regionOptions,
	sameLanguage,
	syncwordsLanguageName,
} from './captionSyncWords'
import { UPGRADE_ROUTE } from './captionLimits'
import { track, EVENTS } from '@/lib/tracking'

/**
 * The settings of a SyncWords caption flow as a controlled form: `modelValue`
 * is the draft from `draftFromFlow`, every edit emits a new draft. Captions
 * come out as Teletext pages in the spoken language plus any translations
 * (each with its engine, null = automatic), up to the plan's `maxLangs` in
 * total; languages whose script Teletext can't show are disabled. Lists,
 * ranges and defaults come from `GET /captions/engine`. Used by the create
 * modal and the manage page's settings panel.
 */
const props = defineProps({
	modelValue: { type: Object, required: true },
	info: { type: Object, default: () => FALLBACK_ENGINE_INFO },
	// { [field]: message }: client validation merged with core's field errors.
	errors: { type: Object, default: () => ({}) },
	disabled: { type: Boolean, default: false },
	// Prefix for element ids, so two forms on a page don't collide.
	idPrefix: { type: String, default: 'sw' },
	// Offer "Automatic" (no region sent: core picks the one nearest the stream).
	autoRegion: { type: Boolean, default: false },
	// The region core picks for Automatic, when known (GET /captions/engine?streamKey=).
	autoRegionCode: { type: String, default: '' },
	// Languages per flow, the spoken one included (`limits.maxLangs`).
	maxLangs: { type: Number, default: 4 },
	// The plan (not the service) sets `maxLangs`: offer the upgrade at the cap.
	planCapped: { type: Boolean, default: false },
	// The languages error came from the plan limit: offer the upgrade with it.
	languagesUpgrade: { type: Boolean, default: false },
	// The saved flow's Teletext pages (`flow.syncwords.pages`), shown next to each language.
	pages: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue'])

const draft = computed(() => props.modelValue)
const fid = (name) => `${props.idPrefix}-${name}`
const sw = computed(() => props.info?.syncwords || FALLBACK_ENGINE_INFO.syncwords)

function set(patch) {
	emit('update:modelValue', { ...draft.value, ...patch })
}

const name = (code) => syncwordsLanguageName(code, props.info)
const spokenOpts = computed(() => withTeletextReasons(withCurrent(spokenOptions(props.info, draft.value.transcriptionType), draft.value.spokenLanguage, props.info)))
// Speechmatics' list is the only one known up front; other engines check the code when saved.
const spokenUnlisted = computed(() => !sw.value.spokenLanguages?.[draft.value.transcriptionType])

// ------------------------------------------------------------ Languages
const translations = computed(() => draft.value.translations || [])
const languageCount = computed(() => 1 + translations.value.length)
const atLimit = computed(() => languageCount.value >= props.maxLangs)
const takenCodes = computed(() => [draft.value.spokenLanguage, ...translations.value.map((t) => t.language)].filter(Boolean))
const translationOpts = computed(() => withTeletextReasons(sw.value.translationLanguages.filter((l) => !sameLanguage(l.code, draft.value.spokenLanguage))))
const pageOf = (code) => props.pages.find((p) => sameLanguage(p.language, code))?.page ?? null
const addLabel = computed(() => (atLimit.value ? 'Limit reached' : 'Add language'))

/** The new spoken language can't also be a translation. */
function onSpokenChange(next) {
	if (!next || next === draft.value.spokenLanguage) return
	set({ spokenLanguage: next, translations: translations.value.filter((t) => !sameLanguage(t.language, next)) })
}

function addTranslation(code) {
	if (!code || atLimit.value || takenCodes.value.some((c) => sameLanguage(c, code))) return
	set({ translations: [...translations.value, { language: code, engine: null }] })
}

function removeTranslation(code) {
	set({ translations: translations.value.filter((t) => t.language !== code) })
}

function setEngine(code, engine) {
	set({ translations: translations.value.map((t) => (t.language === code ? { ...t, engine: engine || null } : t)) })
}

const engineOptions = computed(() => [
	{ value: '', text: 'Auto' },
	...sw.value.translationEngines.map((e) => ({ value: e, text: e })),
])

// ------------------------------------------------------------ Position
const lineChoices = computed(() => {
	const { min, max } = sw.value.ranges.lineCount
	const out = []
	for (let n = min; n <= max; n += 1) out.push(n)
	return out
})
const colorOptions = computed(() => sw.value.textColors.map((c) => ({ value: c, text: c.charAt(0).toUpperCase() + c.slice(1) })))
const bufferRange = computed(() => sw.value.ranges.bufferSec)
const lineRange = computed(() => sw.value.ranges.maxLineLength)

const typeOptions = computed(() => sw.value.transcriptionTypes.map((t) => ({ value: t, text: t })))
const regions = computed(() => regionOptions(props.info, { automatic: props.autoRegion || !draft.value.region, autoRegion: props.autoRegionCode }))

const advancedFields = ['transcriptionType', 'region', 'ingestPassphrase', 'maxLineLength', 'doubleHeight']
const advancedOpen = ref(false)
const showPassphrase = ref(false)
// Core's or the form's error on a field inside Advanced opens it.
watch(() => advancedFields.some((f) => props.errors[f]), (hasError) => { if (hasError) advancedOpen.value = true }, { immediate: true })

const advancedSummary = computed(() => [
	draft.value.transcriptionType,
	draft.value.region ? REGION_NAMES[draft.value.region] || draft.value.region : (props.autoRegionCode ? autoRegionLabel(props.autoRegionCode) : 'Automatic region'),
	draft.value.doubleHeight ? '' : 'single height',
	Number(draft.value.maxLineLength) === lineRange.value.max ? '' : `${draft.value.maxLineLength} characters per line`,
	draft.value.ingestPassphrase ? 'passphrase on' : '',
].filter(Boolean).join(' · '))

function onBuffer(value) {
	set({ bufferSec: value === '' || value === null ? '' : Number(value) })
}

function onLineLength(value) {
	set({ maxLineLength: value === '' || value === null ? '' : Number(value) })
}

const radioClass = (on) => (on ? 'bg-main text-white ring-main' : 'bg-surface-3 text-surface-text-1 ring-white/5 hover:bg-surface-4 hover:text-white')
</script>

<template>
	<div class="flex flex-col gap-4" data-testid="syncwords-settings">
		<div class="flex min-w-0 flex-col gap-2">
			<label :for="fid('spoken')" class="m-0 text-tiny font-medium text-surface-text-1">Spoken language</label>
			<LanguageSelect
				:id="fid('spoken')"
				:model-value="draft.spokenLanguage"
				:options="spokenOpts"
				placeholder="What language is spoken?"
				aria-label="Spoken language"
				:disabled="disabled"
				@update:model-value="onSpokenChange"
			/>
			<p v-if="errors.spokenLanguage" class="m-0 text-xs text-error-light" role="alert">{{ errors.spokenLanguage }}</p>
			<p v-else-if="spokenUnlisted" class="m-0 text-xs text-surface-7">{{ draft.transcriptionType }} checks the language when you save.</p>
		</div>

		<div class="flex min-w-0 flex-col gap-2" data-testid="caption-languages">
			<div class="flex items-center justify-between gap-2">
				<span :id="fid('langs-label')" class="text-tiny font-medium text-surface-text-1">Caption languages</span>
				<span class="text-[11px] tabular-nums text-surface-7">
					<Tooltip :active="planCapped && atLimit" :content="`Your plan includes ${maxLangs} ${maxLangs === 1 ? 'language' : 'languages'} per flow`" wrapper-class="inline-flex">
						<span data-testid="langs-count">{{ languageCount }} of {{ maxLangs }}</span>
					</Tooltip>
					<template v-if="planCapped && atLimit">
						·
						<router-link :to="UPGRADE_ROUTE" class="font-medium text-main-light hover:underline" data-testid="lang-cap-hint" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning-languages' })">Upgrade</router-link>
					</template>
				</span>
			</div>
			<ul class="m-0 flex list-none flex-col gap-1.5 p-0" :aria-labelledby="fid('langs-label')">
				<li class="flex min-h-10 items-center gap-2 rounded-lg bg-surface-3/60 px-3 py-1.5" :data-lang="draft.spokenLanguage" data-testid="caption-language-original">
					<span class="min-w-0 flex-1 truncate text-tiny font-medium text-surface-text-2">{{ name(draft.spokenLanguage) }} · original</span>
					<span v-if="pageOf(draft.spokenLanguage)" class="text-[11px] tabular-nums text-surface-7" data-testid="caption-language-page">page {{ pageOf(draft.spokenLanguage) }}</span>
					<span class="text-[10px] uppercase tracking-wide text-main-light">spoken</span>
				</li>
				<li
					v-for="t in translations"
					:key="t.language"
					class="flex min-h-10 flex-wrap items-center gap-2 rounded-lg bg-surface-3/60 px-3 py-1.5"
					:data-lang="t.language"
					data-testid="caption-language-translation"
				>
					<span class="min-w-0 flex-1 truncate text-tiny font-medium text-surface-text-2">{{ name(t.language) }}</span>
					<span v-if="pageOf(t.language)" class="text-[11px] tabular-nums text-surface-7" data-testid="caption-language-page">page {{ pageOf(t.language) }}</span>
					<span class="text-[10px] uppercase tracking-wide text-surface-7">translated</span>
					<label :for="fid(`engine-${t.language}`)" class="sr-only">Translation engine for {{ name(t.language) }}</label>
					<div class="w-28 shrink-0">
						<FormSelect :id="fid(`engine-${t.language}`)" size="sm" :model-value="t.engine || ''" :options="engineOptions" :disabled="disabled" @update:model-value="(v) => setEngine(t.language, v)" />
					</div>
					<button
						type="button"
						class="flex size-7 shrink-0 items-center justify-center rounded-full text-surface-7 transition-colors hover:bg-surface-4 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
						:aria-label="`Remove ${name(t.language)}`"
						:disabled="disabled"
						@click="removeTranslation(t.language)"
					>
						<icon-base width="10" height="10"><icon-cross /></icon-base>
					</button>
				</li>
			</ul>
			<div>
				<LanguageSelect
					variant="chip"
					:model-value="''"
					:options="translationOpts"
					:disabled-codes="takenCodes"
					:disabled="disabled || atLimit"
					:trigger-label="addLabel"
					aria-label="Add caption language"
					@select="addTranslation"
				>
					<template #trigger-icon>
						<icon-base width="10" height="10" width-view-box="12" height-view-box="12"><icon-plus-tiny /></icon-base>
					</template>
				</LanguageSelect>
			</div>
			<p v-if="errors.translations" class="m-0 text-xs text-error-light" role="alert" data-testid="languages-error">
				{{ errors.translations }}
				<router-link v-if="languagesUpgrade" :to="UPGRADE_ROUTE" class="ml-1 font-medium text-main-light hover:underline" @click="track(EVENTS.UPGRADE_CLICKED, { source: 'captioning-languages' })">Upgrade</router-link>
			</p>
			<p v-else class="m-0 text-xs text-surface-7">Each language is its own Teletext subtitle page. Translations come from the spoken language; Auto picks the engine.</p>
		</div>

		<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<div class="flex flex-col gap-2">
				<span :id="fid('position-label')" class="text-tiny font-medium text-surface-text-1">Caption position</span>
				<div class="flex gap-1.5" role="radiogroup" :aria-labelledby="fid('position-label')" data-testid="caption-position">
					<button
						v-for="option in POSITION_OPTIONS"
						:key="option.value"
						type="button"
						role="radio"
						:aria-checked="draft.position === option.value"
						:disabled="disabled"
						:data-position="option.value"
						class="inline-flex h-8 items-center justify-center rounded-full px-3 text-tiny font-medium ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
						:class="radioClass(draft.position === option.value)"
						@click="set({ position: option.value })"
					>
						{{ option.label }}
					</button>
				</div>
				<p v-if="errors.position" class="m-0 text-xs text-error-light" role="alert" data-testid="position-error">{{ errors.position }}</p>
				<p v-else class="m-0 text-xs text-surface-7">Where captions appear on the picture.</p>
			</div>
			<div class="flex flex-col gap-2">
				<span :id="fid('lines-label')" class="text-tiny font-medium text-surface-text-1">Lines</span>
				<div class="flex gap-1.5" role="radiogroup" :aria-labelledby="fid('lines-label')" data-testid="caption-lines">
					<button
						v-for="n in lineChoices"
						:key="n"
						type="button"
						role="radio"
						:aria-checked="Number(draft.lineCount) === n"
						:aria-label="n === 1 ? '1 line' : `${n} lines`"
						:disabled="disabled"
						class="inline-flex h-8 min-w-10 items-center justify-center rounded-full px-3 text-tiny font-medium ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
						:class="radioClass(Number(draft.lineCount) === n)"
						@click="set({ lineCount: n })"
					>
						{{ n }}
					</button>
				</div>
				<p v-if="errors.lineCount" class="m-0 text-xs text-error-light" role="alert" data-testid="lines-error">{{ errors.lineCount }}</p>
				<p v-else class="m-0 text-xs text-surface-7">Subtitle lines on screen at once.</p>
			</div>
			<div class="flex flex-col gap-1.5">
				<div class="flex items-end gap-2">
					<div class="min-w-0 flex-1">
						<FormSelect :id="fid('text-color')" :model-value="draft.textColor" label="Text colour" :options="colorOptions" :disabled="disabled" @update:model-value="(v) => set({ textColor: v })" />
					</div>
					<span
						class="mb-1.5 size-5 shrink-0 rounded-full ring-1 ring-white/20"
						:style="{ backgroundColor: TEXT_COLOR_SWATCHES[draft.textColor] || '#ffffff' }"
						aria-hidden="true"
						data-testid="text-color-swatch"
					/>
				</div>
				<p v-if="errors.textColor" class="m-0 text-xs text-error-light" role="alert">{{ errors.textColor }}</p>
			</div>
			<div class="flex flex-col gap-2">
				<span :id="fid('alignment-label')" class="text-tiny font-medium text-surface-text-1">Alignment</span>
				<div class="flex gap-1.5" role="radiogroup" :aria-labelledby="fid('alignment-label')" data-testid="caption-alignment">
					<button
						v-for="option in ALIGNMENT_OPTIONS"
						:key="option.value"
						type="button"
						role="radio"
						:aria-checked="draft.alignment === option.value"
						:disabled="disabled"
						class="inline-flex h-8 items-center justify-center rounded-full px-3 text-tiny font-medium ring-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
						:class="radioClass(draft.alignment === option.value)"
						@click="set({ alignment: option.value })"
					>
						{{ option.label }}
					</button>
				</div>
				<p v-if="errors.alignment" class="m-0 text-xs text-error-light" role="alert">{{ errors.alignment }}</p>
			</div>
		</div>

		<div class="flex flex-col gap-2">
			<label :for="fid('buffer')" class="m-0 text-tiny font-medium text-surface-text-1">Delay buffer</label>
			<div class="flex items-center gap-2">
				<input
					:id="fid('buffer')"
					:value="draft.bufferSec"
					type="number"
					inputmode="numeric"
					:min="bufferRange.min"
					:max="bufferRange.max"
					step="1"
					class="h-8 w-20 rounded border-0 bg-surface-3 px-2 text-center text-base tabular-nums text-surface-text-2 focus:bg-surface-4 focus:outline-none focus:ring-0 disabled:text-surface-7 md:text-tiny"
					:class="errors.bufferSec ? 'ring-1 ring-error-light' : ''"
					:aria-invalid="errors.bufferSec ? 'true' : undefined"
					:aria-describedby="fid('buffer-help')"
					:disabled="disabled"
					@input="onBuffer($event.target.value)"
				>
				<span class="text-xs text-surface-8">seconds ({{ bufferRange.min }}–{{ bufferRange.max }})</span>
			</div>
			<p v-if="errors.bufferSec" class="m-0 text-xs text-error-light" role="alert" data-testid="buffer-error">{{ errors.bufferSec }}</p>
			<p :id="fid('buffer-help')" class="m-0 text-xs text-surface-7">{{ BUFFER_HINT }} Viewers see the stream this much later.</p>
		</div>

		<div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
			<div class="flex flex-col gap-1.5">
				<FormSwitch
					:id="fid('diarization')"
					:model-value="draft.diarization"
					size="sm"
					label="Speaker labels"
					label-styles="text-tiny font-medium text-surface-text-1"
					:disabled="disabled"
					@update:model-value="(v) => set({ diarization: v })"
				/>
				<p class="m-0 text-xs text-surface-7">Marks each change of speaker.</p>
			</div>
			<div class="flex flex-col gap-1.5">
				<FormSwitch
					:id="fid('disfluency')"
					:model-value="draft.filterDisfluency"
					size="sm"
					label="Remove filler words"
					label-styles="text-tiny font-medium text-surface-text-1"
					:disabled="disabled"
					@update:model-value="(v) => set({ filterDisfluency: v })"
				/>
				<p class="m-0 text-xs text-surface-7">Drops um, uh and er from captions.</p>
			</div>
		</div>

		<div class="border-t border-surface-3 pt-3">
			<button
				type="button"
				class="inline-flex items-center gap-1.5 rounded text-tiny font-medium text-surface-8 transition-colors hover:text-white"
				:aria-expanded="advancedOpen ? 'true' : 'false'"
				:aria-controls="fid('advanced')"
				data-testid="syncwords-advanced-toggle"
				@click="advancedOpen = !advancedOpen"
			>
				<icon-base width="12" height="12" class="transition-transform" :class="advancedOpen ? '' : '-rotate-90'"><icon-chevron-down /></icon-base>
				Advanced
				<span v-if="!advancedOpen" class="text-[11px] font-normal text-surface-7">· {{ advancedSummary }}</span>
			</button>
			<div v-show="advancedOpen" :id="fid('advanced')" class="mt-3 flex flex-col gap-4" data-testid="syncwords-advanced">
				<!-- Stacked: the region labels (e.g. "Automatic (nearest to your stream)") need the full width. -->
				<div class="grid grid-cols-1 gap-4">
					<div class="flex flex-col gap-1.5">
						<FormSelect :id="fid('engine')" :model-value="draft.transcriptionType" label="Speech engine" :options="typeOptions" :disabled="disabled" @update:model-value="(v) => set({ transcriptionType: v })" />
						<p v-if="errors.transcriptionType" class="m-0 text-xs text-error-light" role="alert">{{ errors.transcriptionType }}</p>
					</div>
					<div class="flex flex-col gap-1.5">
						<FormSelect :id="fid('region')" :model-value="draft.region" label="Region" :options="regions" :disabled="disabled" @update:model-value="(v) => set({ region: v })" />
						<p v-if="errors.region" class="m-0 text-xs text-error-light" role="alert">{{ errors.region }}</p>
						<p v-else-if="!draft.region" class="m-0 text-xs text-surface-7">Picked for you, nearest to your stream.</p>
						<p v-else class="m-0 text-xs text-surface-7">Closest to your encoder. Changing it can change the ingest URL.</p>
					</div>
					<div class="flex flex-col gap-1.5">
						<FormSwitch
							:id="fid('double-height')"
							:model-value="draft.doubleHeight"
							size="sm"
							label="Double height"
							label-styles="text-tiny font-medium text-surface-text-1"
							:disabled="disabled"
							@update:model-value="(v) => set({ doubleHeight: v })"
						/>
						<p class="m-0 text-xs text-surface-7">Taller, easier-to-read letters; each line takes two Teletext rows.</p>
					</div>
					<div class="flex flex-col gap-1.5">
						<label :for="fid('line-length')" class="m-0 text-tiny font-medium text-surface-text-1">Max line length</label>
						<div class="flex items-center gap-2">
							<input
								:id="fid('line-length')"
								:value="draft.maxLineLength"
								type="number"
								inputmode="numeric"
								:min="lineRange.min"
								:max="lineRange.max"
								step="1"
								class="h-8 w-20 rounded border-0 bg-surface-3 px-2 text-center text-base tabular-nums text-surface-text-2 placeholder:text-surface-7 focus:bg-surface-4 focus:outline-none focus:ring-0 disabled:text-surface-7 md:text-tiny"
								:class="errors.maxLineLength ? 'ring-1 ring-error-light' : ''"
								:aria-invalid="errors.maxLineLength ? 'true' : undefined"
								:disabled="disabled"
								@input="onLineLength($event.target.value)"
							>
							<span class="text-xs text-surface-8">characters ({{ lineRange.min }}–{{ lineRange.max }})</span>
						</div>
						<p v-if="errors.maxLineLength" class="m-0 text-xs text-error-light" role="alert" data-testid="line-length-error">{{ errors.maxLineLength }}</p>
						<p v-else class="m-0 text-xs text-surface-7">Characters per Teletext line; {{ lineRange.max }} is the full width.</p>
					</div>
				</div>
				<div class="flex flex-col gap-1.5">
					<label :for="fid('passphrase')" class="m-0 text-tiny font-medium text-surface-text-1">Ingest passphrase <span class="font-normal text-surface-7">(optional)</span></label>
					<div class="relative">
						<FormInput
							:id="fid('passphrase')"
							:model-value="draft.ingestPassphrase"
							:type="showPassphrase ? 'text' : 'password'"
							placeholder="No passphrase"
							custom-classes="!pr-10 font-mono"
							:is-error="!!errors.ingestPassphrase"
							:disabled="disabled"
							:maxlength="79"
							@update:model-value="(v) => set({ ingestPassphrase: v })"
						/>
						<button
							type="button"
							class="absolute right-1 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded text-surface-7 hover:text-white"
							:aria-label="showPassphrase ? 'Hide passphrase' : 'Show passphrase'"
							@click="showPassphrase = !showPassphrase"
						>
							<icon-base width="16" height="16" fill="none">
								<icon-eye-crossed v-if="showPassphrase" />
								<icon-eye v-else />
							</icon-base>
						</button>
					</div>
					<p v-if="errors.ingestPassphrase" class="m-0 text-xs text-error-light" role="alert" data-testid="passphrase-error">{{ errors.ingestPassphrase }}</p>
					<p v-else class="m-0 text-xs text-surface-7">Encrypts the SRT link from your encoder; set the same passphrase there. {{ PASSPHRASE_RULE }}</p>
				</div>
			</div>
		</div>
	</div>
</template>
